import React, { useRef, useState } from 'react'
import { Breadcrumbs, DatePickerRange, FlashMessage, GlobalLoader, InputField, ModalAlert, NumberInput, SelectInput } from '@reactiveonline/reactive_admin_shared-packages'
import moment from 'moment'
import Toggle from 'react-toggle'
import GlobalImports from '../shared/GlobalImports'
import Drawer from '../shared/Drawer'

export default function Edit({ affiliateProgramEnabled, appProps, breadcrumbs, organization, organizationPath, updatePath }) {
  const [organizationState, setOrganizationState] = useState({
    ...organization,
    fees: organization.fees.map(fee => ({
      ...fee,
      timeFrames: fee.timeFrames.map(timeFrame => ({
        ...timeFrame,
        startDate: moment(timeFrame.startDate),
        endDate: moment(timeFrame.endDate)
      }))
    }))
  })
  const [currentlySelectedFee, setCurrentlySelectedFee] = useState(null)
  const [feeSnapshot, setFeeSnapshot] = useState(null)
  const [feeToBeDeleted, setFeeToBeDeleted] = useState(null)
  const [loading, setLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const flashMessageRef = useRef(null)

  const markSaveable = () => setHasUnsavedChanges(true)
  const feeTranslations = Partners.translations.fee
  const sourceOptions = Object.entries(feeTranslations.sources)
    .filter(([value]) => affiliateProgramEnabled || value !== 'affiliate_code')
    .map(([value, title]) => ({ value, title }))
  const payoutTriggerOptions = Object.entries(feeTranslations.payout_triggers).map(([value, title]) => ({ value, title }))
  const calculationTypeOptions = Object.entries(feeTranslations.calculation_types).map(([value, title]) => ({ value, title }))
  const feeLabel = (options, value) => options.find(option => option.value === value)?.title || value

  const normalizeOrganization = updatedOrganization => ({
    ...updatedOrganization,
    fees: updatedOrganization.fees.map(fee => ({
      ...fee,
      timeFrames: fee.timeFrames.map(timeFrame => ({
        ...timeFrame,
        startDate: moment(timeFrame.startDate),
        endDate: moment(timeFrame.endDate)
      }))
    }))
  })

  const handleSave = (fees = organizationState.fees, options = {}) => {
    const fd = new FormData()
    const feesToSave = affiliateProgramEnabled ? fees : fees.filter(fee => fee.source !== 'affiliate_code')

    feesToSave.forEach((fee, index) => {
      if (fee.id > 0) {
        fd.append(`organization[fees_attributes][${ index }][id]`, fee.id)
      }

      if (fee._destroy) {
        fd.append(`organization[fees_attributes][${ index }][_destroy]`, true)
      } else {
        fd.append(`organization[fees_attributes][${ index }][source]`, fee.source)
        fd.append(`organization[fees_attributes][${ index }][payout_trigger]`, fee.payoutTrigger)
        fd.append(`organization[fees_attributes][${ index }][calculation_type]`, fee.calculationType)
        fd.append(`organization[fees_attributes][${ index }][currency]`, fee.currency)
        fd.append(`organization[fees_attributes][${ index }][active]`, fee.active)
        fd.append(`organization[fees_attributes][${ index }][percentage]`, fee.percentage)
        if (fee.fixedAmount !== null && fee.fixedAmount !== undefined && fee.fixedAmount !== '') {
          fd.append(`organization[fees_attributes][${ index }][fixed_amount_cents]`, Math.round(parseFloat(fee.fixedAmount) * 100))
        }

        fee.timeFrames.forEach((timeFrame, idx) => {
          if (timeFrame.id > 0) {
            fd.append(`organization[fees_attributes][${ index }][time_frames_attributes][${ idx }][id]`, timeFrame.id)
          }

          if (timeFrame._destroy) {
            fd.append(`organization[fees_attributes][${ index }][time_frames_attributes][${ idx }][_destroy]`, true)
          } else {
            fd.append(`organization[fees_attributes][${ index }][time_frames_attributes][${ idx }][start_date]`, timeFrame.startDate.format('YYYY-MM-DD'))
            fd.append(`organization[fees_attributes][${ index }][time_frames_attributes][${ idx }][end_date]`, timeFrame.endDate.format('YYYY-MM-DD'))
          }
        })
      }
    })

    setLoading(true)

    Rails.ajax({
      type: 'PATCH',
      url: updatePath,
      dataType: 'json',
      data: fd,
      success: res => {
        setOrganizationState(normalizeOrganization(res.organization))
        setHasUnsavedChanges(false)
        setFeeSnapshot(null)
        if (options.closeDrawer !== false) setCurrentlySelectedFee(null)
        setLoading(false)
        flashMessageRef.current.show(Partners.translations.fee.update_success)
      },
      error: res => {
        setLoading(false)
        flashMessageRef.current.show(res.errors, 'error')
      }
    })
  }

  const handleAddFee = () => {
    markSaveable()

    const newFeeId = Math.min(...organizationState.fees.map(fee => fee.id), 0) - 1

    setOrganizationState(prevState => ({
      ...prevState,
      fees: [
        ...prevState.fees,
        {
          id: newFeeId,
          source: 'all',
          payoutTrigger: 'paid_customer',
          calculationType: 'percentage',
          percentage: '0.00',
          fixedAmount: '',
          fixedAmountCents: null,
          currency: 'EUR',
          active: true,
          timeFrames: [
            {
              id: -1,
              startDate: moment(new Date()),
              endDate: moment(new Date())
            }
          ]
        }
      ]
    }))

    setCurrentlySelectedFee(newFeeId)
    setFeeSnapshot(null)
  }

  const openEditFeeDrawer = fee => {
    setFeeSnapshot(JSON.parse(JSON.stringify(fee)))
    setCurrentlySelectedFee(fee.id)
  }

  const deletedFees = feeId => {
    const relevantFeeIndex = organizationState.fees.findIndex(fee => fee.id === feeId)

    if (feeId < 0) {
      return [
        ...organizationState.fees.slice(0, relevantFeeIndex),
        ...organizationState.fees.slice(relevantFeeIndex + 1)
      ]
    }

    return [
      ...organizationState.fees.slice(0, relevantFeeIndex),
      {
        id: organizationState.fees[relevantFeeIndex].id,
        _destroy: true
      },
      ...organizationState.fees.slice(relevantFeeIndex + 1)
    ]
  }

  const handleDeleteFeeClick = feeId => {
    setFeeToBeDeleted(feeId)
  }

  const handleDeleteFee = () => {
    const fees = deletedFees(feeToBeDeleted)

    setFeeToBeDeleted(null)
    setOrganizationState(prevState => ({
      ...prevState,
      fees: fees
    }))
    setHasUnsavedChanges(false)
    handleSave(fees)
  }

  const handleUpdateFee = ({ feeId, source, payoutTrigger, calculationType, percentageValue, fixedAmountValue, currency, active, timeFrames }) => {
    const relevantFeeIndex = organizationState.fees.findIndex(fee => fee.id === feeId)

    markSaveable()

    setOrganizationState(prevState => ({
      ...prevState,
      fees: [
        ...prevState.fees.slice(0, relevantFeeIndex),
        {
          ...prevState.fees[relevantFeeIndex],
          source: source ?? prevState.fees[relevantFeeIndex].source,
          payoutTrigger: payoutTrigger ?? prevState.fees[relevantFeeIndex].payoutTrigger,
          calculationType: calculationType ?? prevState.fees[relevantFeeIndex].calculationType,
          percentage: percentageValue ?? prevState.fees[relevantFeeIndex].percentage,
          fixedAmount: fixedAmountValue ?? prevState.fees[relevantFeeIndex].fixedAmount,
          currency: currency ?? prevState.fees[relevantFeeIndex].currency,
          active: active ?? prevState.fees[relevantFeeIndex].active,
          timeFrames: timeFrames ?? prevState.fees[relevantFeeIndex].timeFrames
        },
        ...prevState.fees.slice(relevantFeeIndex + 1)
      ]
    }))
  }

  const handleDeleteTimeFrame = (feeId, timeFrameId) => {
    const relevantFeeIndex = organizationState.fees.findIndex(fee => fee.id === feeId)
    const relevantTimeFrameIndex = organizationState.fees[relevantFeeIndex].timeFrames.findIndex(timeFrame => timeFrame.id === timeFrameId)

    const resultingTimeFrameArray = timeFrameId < 0 ? [
      ...organizationState.fees[relevantFeeIndex].timeFrames.slice(0, relevantTimeFrameIndex),
      ...organizationState.fees[relevantFeeIndex].timeFrames.slice(relevantTimeFrameIndex + 1)
    ] : [
      ...organizationState.fees[relevantFeeIndex].timeFrames.slice(0, relevantTimeFrameIndex),
      {
        id: organizationState.fees[relevantFeeIndex].timeFrames[relevantTimeFrameIndex].id,
        _destroy: true
      },
      ...organizationState.fees[relevantFeeIndex].timeFrames.slice(relevantTimeFrameIndex + 1)
    ]

    handleUpdateFee({ feeId: feeId, timeFrames: resultingTimeFrameArray })
  }

  const handleAddTimeFrame = () => {
    const selectedFee = organizationState.fees.find(fee => fee.id === currentlySelectedFee)
    const resultingTimeFrameArray = [
      ...selectedFee.timeFrames,
      {
        id: Math.min(...selectedFee.timeFrames.map(timeFrame => timeFrame.id), 0) - 1,
        startDate: moment(new Date()),
        endDate: moment(new Date())
      }
    ]

    handleUpdateFee({ feeId: currentlySelectedFee, timeFrames: resultingTimeFrameArray })
  }

  const handleUpdateTimeFrame = (timeFrameId, dateFrom, dateTo) => {
    const relevantFeeIndex = organizationState.fees.findIndex(fee => fee.id === currentlySelectedFee)
    const relevantTimeFrameIndex = organizationState.fees[relevantFeeIndex].timeFrames.findIndex(timeFrame => timeFrame.id === timeFrameId)

    const resultingTimeFrameArray = [
      ...organizationState.fees[relevantFeeIndex].timeFrames.slice(0, relevantTimeFrameIndex),
      {
        ...organizationState.fees[relevantFeeIndex].timeFrames[relevantTimeFrameIndex],
        startDate: moment(dateFrom),
        endDate: moment(dateTo)
      },
      ...organizationState.fees[relevantFeeIndex].timeFrames.slice(relevantTimeFrameIndex + 1)
    ]

    handleUpdateFee({ feeId: currentlySelectedFee, timeFrames: resultingTimeFrameArray })
  }

  const closeEditFeeDrawer = () => {
    if (currentlySelectedFee < 0) {
      setOrganizationState(prevState => ({
        ...prevState,
        fees: prevState.fees.filter(fee => fee.id !== currentlySelectedFee)
      }))
      setHasUnsavedChanges(false)
    } else if (feeSnapshot) {
      setOrganizationState(prevState => ({
        ...prevState,
        fees: prevState.fees.map(fee => fee.id === currentlySelectedFee ? {
          ...feeSnapshot,
          timeFrames: feeSnapshot.timeFrames.map(timeFrame => ({
            ...timeFrame,
            startDate: moment(timeFrame.startDate),
            endDate: moment(timeFrame.endDate)
          }))
        } : fee)
      }))
      setHasUnsavedChanges(false)
    }

    setFeeSnapshot(null)
    setCurrentlySelectedFee(null)
  }

  const activeFees = organizationState.fees.filter(fee => !fee._destroy && (affiliateProgramEnabled || fee.source !== 'affiliate_code'))
  const selectedFee = organizationState.fees.find(fee => fee.id === currentlySelectedFee)
  const activeTimeFrameCount = activeFees.reduce((total, fee) => (
    total + fee.timeFrames.filter(timeFrame => !timeFrame._destroy).length
  ), 0)

  return (
    <>
      <div className='page-wrapper-header'>
        <div style={{ width: '100%' }}>
          <div className='flex-box items-center content-space-between flex-wrap' style={{ gap: 20 }}>
            <div className='page-header'>
              <i className='fa-light fa-percent' />
              <h1 className='page-header-title'>
                { Partners.translations.fee.plural }
              </h1>
            </div>
          </div>

          <Breadcrumbs
            breadcrumbs={ breadcrumbs }
            wrapperHeader={ false }
          />
        </div>
      </div>

      <section className='m-t-50'>
        <div className='m-b-20'>
          <div className='flex-box items-center content-space-between flex-wrap' style={{ gap: 20, width: '100%' }}>
            <div>
              <div className='bold' style={{ fontSize: 22 }}>
                { organization.name }
              </div>
              <div style={{ color: '#697386', fontSize: 14, lineHeight: 1.45, marginTop: 6 }}>
                { Partners.translations.fee.percentage_help }
              </div>
            </div>

            <div className='flex-box items-center' style={{ gap: 12 }}>
              <div style={{ background: '#f6f8fb', border: '1px solid #e7eaf0', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ color: '#697386', fontSize: 12 }}>
                  { Partners.translations.fee.plural }
                </div>
                <div className='bold' style={{ fontSize: 18 }}>
                  { activeFees.length }
                </div>
              </div>

              <div style={{ background: '#f6f8fb', border: '1px solid #e7eaf0', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ color: '#697386', fontSize: 12 }}>
                  { Partners.translations.fee.applicable_for }
                </div>
                <div className='bold' style={{ fontSize: 18 }}>
                  { activeTimeFrameCount }
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='flex-box flex-column' style={{ gap: 16 }}>
          { activeFees.map((fee, index) => (
            <React.Fragment key={ fee.id || index }>
              <div
                className='flex-box content-space-between'
                style={{ background: '#ffffff', border: '1px solid #e7eaf0', borderRadius: 8, boxShadow: '0 8px 20px rgba(15, 23, 42, .05)', gap: 24, padding: 20 }}
              >
                <div className='flex-1' style={{ minWidth: 0 }}>
                  <div className='flex-box items-center flex-wrap m-b-20' style={{ gap: 12 }}>
                    <div
                      className='flex-box items-center content-center'
                      style={{ background: '#eef5ff', borderRadius: 8, color: '#1f5fbf', height: 42, width: 42 }}
                    >
                      <i className='fa-light fa-percent' />
                    </div>

                    <div>
                      <div style={{ color: '#697386', fontSize: 12 }}>
                        { fee.calculationType === 'percentage' ? feeTranslations.attributes.percentage : feeTranslations.fixed_amount }
                      </div>
                      <div className='bold' style={{ fontSize: 24, lineHeight: 1.15 }}>
                        { fee.calculationType === 'percentage' ? fee.percentage : `${ fee.fixedAmount || '0.00' } ${ fee.currency }` }
                      </div>
                    </div>
                  </div>

                  <div className='flex-box flex-wrap m-b-20' style={{ gap: 8 }}>
                    <div style={{ background: '#eef0f4', borderRadius: 8, color: '#344054', fontSize: 12, padding: '6px 10px' }}>
                      { feeLabel(sourceOptions, fee.source) }
                    </div>
                    <div style={{ background: '#eef0f4', borderRadius: 8, color: '#344054', fontSize: 12, padding: '6px 10px' }}>
                      { feeLabel(payoutTriggerOptions, fee.payoutTrigger) }
                    </div>
                    <div style={{ background: '#eef0f4', borderRadius: 8, color: '#344054', fontSize: 12, padding: '6px 10px' }}>
                      { feeLabel(calculationTypeOptions, fee.calculationType) }
                    </div>
                    <div style={{ background: fee.active ? '#eaf7ef' : '#fff1f0', borderRadius: 8, color: fee.active ? '#277a46' : '#b42318', fontSize: 12, padding: '6px 10px' }}>
                      { fee.active ? feeTranslations.active : feeTranslations.inactive }
                    </div>
                  </div>

                  <div>
                    <div className='bold m-b-10' style={{ color: '#344054', fontSize: 13 }}>
                      { Partners.translations.fee.applicable_for }
                    </div>

                    <div className='flex-box flex-column' style={{ gap: 8 }}>
                      { fee.timeFrames.filter(timeFrame => !timeFrame._destroy).map((timeFrame, idx) => (
                        <div
                          key={ idx }
                          className='flex-box items-center content-space-between flex-wrap'
                          style={{ background: '#f9fafb', border: '1px solid #eef0f4', borderRadius: 8, gap: 12, padding: '10px 12px' }}
                        >
                          <div className='flex-box items-center' style={{ gap: 8 }}>
                            <i className='fa-light fa-calendar-days' style={{ color: '#697386' }} />
                            <span className='bold' style={{ fontSize: 13 }}>
                              { timeFrame.startDate.format('DD/MM/YYYY') }
                            </span>
                          </div>

                          <i className='fa-light fa-arrow-right' style={{ color: '#98a2b3', fontSize: 12 }} />

                          <div className='flex-box items-center' style={{ gap: 8 }}>
                            <i className='fa-light fa-calendar-check' style={{ color: '#697386' }} />
                            <span className='bold' style={{ fontSize: 13 }}>
                              { timeFrame.endDate.format('DD/MM/YYYY') }
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className='flex-box items-start' style={{ gap: 10 }}>
                  <button
                    type='button'
                    className='btn reverted'
                    onClick={ _e => openEditFeeDrawer(fee) }
                    title={ Partners.translations.fee.edit_fee }
                  >
                    <i
                      className='fa-light fa-pencil'
                      style={{ fontSize: '16px' }}
                    />
                  </button>

                  <button
                    type='button'
                    className='btn reverted'
                    onClick={ _e => handleDeleteFeeClick(fee.id) }
                    title={ ReactiveCore.translations.delete_selected }
                  >
                    <i
                      className='fa-light fa-trash'
                      style={{ fontSize: '16px' }}
                    />
                  </button>
                </div>
              </div>
            </React.Fragment>
          ))}

          { activeFees.length === 0 &&
            <div
              className='flex-box flex-column items-center content-center'
              style={{ background: '#f9fafb', border: '1px dashed #d7dce5', borderRadius: 8, color: '#697386', minHeight: 180, padding: 24, textAlign: 'center' }}
            >
              <i className='fa-light fa-percent' style={{ fontSize: 32, marginBottom: 12 }} />
              <div className='bold' style={{ color: '#344054', fontSize: 16 }}>
                { Partners.translations.fee.no_fees }
              </div>
            </div>
          }

          <div className='flex-box content-end m-t-50'>
            <button
              type='button'
              className='btn'
              onClick={ handleAddFee }
            >
              { Partners.translations.fee.add_new_fee }
            </button>
          </div>
        </div>
      </section>

      <Drawer
        visible={ !!currentlySelectedFee && !!selectedFee }
        closeDrawer={ closeEditFeeDrawer }
        title={ Partners.translations.fee.edit_fee }
        subtitle={ Partners.translations.fee.percentage_help }
        action={ handleSave }
        actionText={ ReactiveCore.translations.save }
        disableAction={ !hasUnsavedChanges || loading }
      >
        { !!selectedFee &&
          <div className='flex-box flex-column flex-1'>
            <InputField
              label={ feeTranslations.source }
              classes='m-t-0'
            >
              <SelectInput
                options={ sourceOptions }
                currentOption={ selectedFee.source }
                setCurrentOption={ option => handleUpdateFee({ feeId: currentlySelectedFee, source: option.value }) }
              />
            </InputField>

            <InputField
              label={ feeTranslations.payout_trigger }
            >
              <SelectInput
                options={ payoutTriggerOptions }
                currentOption={ selectedFee.payoutTrigger }
                setCurrentOption={ option => handleUpdateFee({
                  feeId: currentlySelectedFee,
                  payoutTrigger: option.value,
                  calculationType: selectedFee.calculationType === 'percentage' && option.value === 'registration' ? 'fixed_amount' : selectedFee.calculationType
                }) }
              />
            </InputField>

            <InputField
              label={ feeTranslations.calculation_type }
            >
              <SelectInput
                options={ calculationTypeOptions }
                currentOption={ selectedFee.calculationType }
                setCurrentOption={ option => handleUpdateFee({
                  feeId: currentlySelectedFee,
                  calculationType: option.value,
                  payoutTrigger: option.value === 'percentage' ? 'paid_customer' : selectedFee.payoutTrigger
                }) }
              />
            </InputField>

            { selectedFee.calculationType === 'percentage' &&
              <InputField
                label={ `${ Partners.translations.fee.attributes.percentage } (< 1)` }
                help={ Partners.translations.fee.percentage_help }
              >
                <NumberInput
                  number={ selectedFee.percentage }
                  setNumber={ value => handleUpdateFee({ feeId: currentlySelectedFee, percentageValue: value }) }
                  type='decimal'
                  decimalPrecision={ 2 }
                  step={ 0.01 }
                  max={ 0.99 }
                />
              </InputField>
            }

            { selectedFee.calculationType === 'fixed_amount' &&
              <div className='flex-box' style={{ gap: 12 }}>
                <InputField
                  className='flex-1'
                  label={ feeTranslations.fixed_amount }
                >
                  <NumberInput
                    number={ selectedFee.fixedAmount || 0 }
                    setNumber={ value => handleUpdateFee({ feeId: currentlySelectedFee, fixedAmountValue: value }) }
                    type='decimal'
                    decimalPrecision={ 2 }
                    step={ 0.01 }
                  />
                </InputField>

                <InputField
                  className='flex-1'
                  label={ feeTranslations.currency }
                >
                  <input
                    type='text'
                    value={ selectedFee.currency }
                    onChange={ e => handleUpdateFee({ feeId: currentlySelectedFee, currency: e.target.value.toUpperCase() }) }
                  />
                </InputField>
              </div>
            }

            <InputField
              label={ feeTranslations.active }
            >
              <Toggle
                checked={ selectedFee.active }
                onChange={ e => handleUpdateFee({ feeId: currentlySelectedFee, active: e.target.checked }) }
              />
            </InputField>

            <div>
              <div className='bold m-b-10' style={{ color: '#344054', fontSize: 13 }}>
                { `${ Partners.translations.fee.applicable_for }:` }
              </div>

              <div className='flex-box flex-column' style={{ gap: 14 }}>
                { selectedFee.timeFrames.filter(timeFrame => !timeFrame._destroy).map((timeFrame, idx) => (
                  <div
                    key={ idx }
                    className='flex-box content-space-between items-center'
                    style={{ background: '#f9fafb', border: '1px solid #eef0f4', borderRadius: 8, gap: 16, padding: 12 }}
                  >
                    <div className='date-picker-wrapper flex-1'>
                      <DatePickerRange
                        dateFrom={ timeFrame.startDate }
                        dateTo={ timeFrame.endDate }
                        disableClearFrom
                        disableClearTo
                        allowEmptyTo={ false }
                        dateFromTitle={ Partners.translations.fee.valid_from }
                        dateToTitle={ Partners.translations.fee.valid_to }
                        onChange={ (dateFrom, dateTo) => handleUpdateTimeFrame(timeFrame.id, dateFrom, dateTo) }
                      />
                    </div>

                    <button
                      type='button'
                      className='btn'
                      onClick={ _e => handleDeleteTimeFrame(currentlySelectedFee, timeFrame.id) }
                      title={ ReactiveCore.translations.delete_selected }
                    >
                      <i
                        className='fa-light fa-trash'
                        style={{ fontSize: '16px' }}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className='flex-box content-end m-t-50'>
              <button
                type='button'
                className='btn'
                onClick={ handleAddTimeFrame }
              >
                { Partners.translations.fee.add_new_period }
              </button>
            </div>
          </div>
        }
      </Drawer>

      { !!feeToBeDeleted &&
        <ModalAlert
          alert={ Partners.translations.fee.delete_confirmation }
          onSelect={ handleDeleteFee }
          onClose={ () => setFeeToBeDeleted(null) }
        />
      }

      <FlashMessage ref={ flashMessageRef } />

      { loading && <GlobalLoader fullPage /> }

      <GlobalImports appProps={ appProps } />
    </>
  )
}
